/**
 * Blog post config — each post is a structured article rendered by BlogPostPage.
 * Designed for SEO ranking + AI-search readiness (clear headings, FAQs, definitions).
 *
 * Schema:
 *   slug, title, h1, description, keywords, author, published, updated, readMin,
 *   category, hero (one-line teaser), tableOfContents, sections[], faqs[], related[]
 *
 * sections[] supports: { kind: 'p' | 'h2' | 'h3' | 'ul' | 'ol' | 'callout' | 'table' | 'quote', ... }
 */

const TODAY = "2026-05-02";

export const BLOG_POSTS = [
  // ───────────────────────────────────────────── 1
  {
    slug: "how-to-make-gst-invoice-in-india",
    title: "How to Make a GST Invoice in India — Step-by-Step Guide (2026)",
    h1: "How to Make a GST Invoice in India",
    description:
      "Step-by-step guide to creating a GST-compliant tax invoice in India. Learn the 16 mandatory fields, CGST vs IGST split, HSN codes, and common mistakes to avoid.",
    keywords: "how to make gst invoice, gst invoice india, gst invoice format, gst billing guide",
    author: "InvoiceSetu Team",
    published: TODAY,
    updated: TODAY,
    readMin: 6,
    category: "GST Compliance",
    hero: "A complete walkthrough — from generating your first GST invoice to filing returns.",
    sections: [
      { kind: "p", text: "Whether you sell goods, services, or both — if your turnover crosses ₹20 lakh (₹10 lakh in special category states), you must issue a GST tax invoice. Below is the simplest path to creating one that's auditor-approved on the first try." },
      { kind: "h2", text: "What is a GST invoice?" },
      { kind: "p", text: "A GST invoice is a document issued by a GST-registered seller to a buyer that records the sale of goods or services. It's the basis for the buyer to claim Input Tax Credit (ITC) and for the seller to file GSTR-1." },
      { kind: "h2", text: "The 16 mandatory fields" },
      { kind: "ol", items: [
        "Name, address & GSTIN of the supplier",
        "Consecutive serial number (max 16 chars)",
        "Date of issue",
        "Name, address & GSTIN of the recipient (B2B) or only name+address+state (B2C ≥ ₹50,000)",
        "HSN/SAC code (4 digits if turnover < ₹5 Cr; 6 digits if ≥ ₹5 Cr)",
        "Description of goods or services",
        "Quantity (with unit) for goods",
        "Total value of supply",
        "Taxable value (after discount)",
        "Rate of tax (CGST + SGST or IGST)",
        "Amount of tax charged",
        "Place of supply (state name + state code)",
        "Address of delivery (if different from billing)",
        "Whether reverse charge applies",
        "Signature / digital signature of supplier",
        "QR code (for ₹5 Cr+ turnover under e-invoicing)",
      ] },
      { kind: "callout", tone: "blue", title: "Pro tip", text: "Skip even ONE of these fields and the buyer cannot claim ITC. Use a tool that auto-validates before you send — like InvoiceSetu's invoice builder." },
      { kind: "h2", text: "CGST + SGST vs IGST: how to know which to apply" },
      { kind: "p", text: "If the supplier and the recipient are in the SAME state (intra-state), you split the GST into CGST (Central) + SGST (State) — each at half the total rate. If the recipient is in a DIFFERENT state (inter-state), you charge IGST at the full rate. Mixing the two on one invoice is a common audit red flag." },
      { kind: "h2", text: "Step-by-step: creating your first GST invoice" },
      { kind: "ol", items: [
        "Set up your GSTIN, business address and signature in your invoicing tool.",
        "Add the customer with their full GSTIN — the tool detects state automatically.",
        "Add line items with HSN/SAC codes and applicable GST rate (5% / 12% / 18% / 28%).",
        "Apply discount (line-level or overall) BEFORE GST is calculated.",
        "Review the place of supply, totals, and signature.",
        "Generate the PDF and send via Email / WhatsApp share link.",
      ] },
      { kind: "h2", text: "Common mistakes" },
      { kind: "ul", items: [
        "Wrong place of supply → wrong tax type → ITC denied",
        "Out-of-sequence invoice numbers → flagged in GSTR-1",
        "Missing HSN summary above ₹5 Cr turnover",
        "Adding GST on a Bill of Supply (composition / non-GST sellers)",
        "Mixing CGST/SGST and IGST on the same invoice",
      ] },
      { kind: "h2", text: "What's next?" },
      { kind: "p", text: "Once invoices flow, the next priorities are GSTR-1 filing (monthly/quarterly), e-invoicing (mandatory above ₹5 Cr) and reconciling GSTR-2B with your purchase register. Your accountant will thank you for picking a tool that handles these end-to-end." },
    ],
    faqs: [
      { q: "Can I issue a GST invoice without a GSTIN?", a: "No. GSTIN is mandatory for issuing a Tax Invoice. Without one, you must issue a Bill of Supply." },
      { q: "How long should I keep GST invoices?", a: "At least 6 years from the relevant due date for filing the annual return — that's the GST Act requirement." },
      { q: "Can I edit a GST invoice after issuing?", a: "No, you must issue a Credit Note or Debit Note to amend the original invoice. The original cannot be silently changed — that's an audit violation." },
      { q: "Is digital signature mandatory?", a: "Not mandatory for invoices below ₹5 Cr turnover, but highly recommended for tamper-proofing. Above ₹5 Cr e-invoicing thresholds, IRN/QR is mandatory." },
    ],
    related: ["difference-between-bill-and-tax-invoice", "what-is-hsn-code", "gstr1-basics-for-business-owners"],
  },
  // ───────────────────────────────────────────── 2
  {
    slug: "difference-between-bill-and-tax-invoice",
    title: "Bill vs Tax Invoice — What's the Difference? (India)",
    h1: "Difference Between a Bill and a Tax Invoice",
    description:
      "Understand the key differences between a Bill of Supply, regular Bill, and a Tax Invoice in India — and which one your business should issue.",
    keywords: "bill vs tax invoice, bill of supply, tax invoice india, gst invoice difference",
    author: "InvoiceSetu Team",
    published: TODAY,
    updated: TODAY,
    readMin: 4,
    category: "GST Compliance",
    hero: "If you're using the wrong document, you're either over-charging GST or denying your customer ITC. Here's how to fix it.",
    sections: [
      { kind: "p", text: "Indian small business owners often use 'bill' and 'invoice' interchangeably. Under GST, they're legally distinct — and using the wrong one can cost you ITC, penalties, or both." },
      { kind: "h2", text: "Quick definitions" },
      { kind: "ul", items: [
        "Tax Invoice — issued by a GST-registered supplier for a taxable supply. Charges CGST/SGST or IGST. Buyer can claim ITC.",
        "Bill of Supply — issued by a non-GST supplier OR a composition scheme dealer OR for exempt goods. NO GST charged. Buyer CANNOT claim ITC.",
        "Bill / Cash Memo — informal day-to-day receipt; no legal weight under GST unless it qualifies as one of the above.",
      ] },
      { kind: "h2", text: "When to issue which?" },
      { kind: "table", headers: ["Scenario", "Document", "GST Charged?"], rows: [
        ["You're GST-registered, selling to anyone", "Tax Invoice", "Yes"],
        ["You're under ₹20L turnover, no GSTIN", "Bill of Supply", "No"],
        ["You're a Composition Dealer", "Bill of Supply (with note)", "No"],
        ["You sell only exempt goods (e.g. fresh produce)", "Bill of Supply", "No"],
        ["You sell B2C below ₹50,000 cash", "Tax Invoice (recipient details optional)", "Yes"],
      ]},
      { kind: "callout", tone: "amber", title: "Avoid this mistake", text: "Composition dealers must add a note: 'Composition taxable person, not eligible to collect tax on supplies'. Skipping this is a ₹10,000 penalty." },
      { kind: "h2", text: "How does the right tool help?" },
      { kind: "p", text: "Tools like InvoiceSetu auto-detect your registration status from your settings — leave your GSTIN empty and the system switches every document to a compliant Bill of Supply. Add a GSTIN and it flips to Tax Invoice mode instantly." },
    ],
    faqs: [
      { q: "Can I claim Input Tax Credit on a Bill of Supply?", a: "No. Only Tax Invoices entitle the buyer to ITC. This is the single biggest reason to be careful which document you issue." },
      { q: "If I cross ₹20L turnover, what changes?", a: "You must register for GST, then start issuing Tax Invoices. Old Bills of Supply remain valid but cannot be retroactively converted." },
      { q: "Is a 'Cash Memo' the same as a Bill of Supply?", a: "Not necessarily — a cash memo is informal. To be legally valid for non-GST sellers, it must contain Bill of Supply mandatory fields (your name, address, customer name, items, total, date)." },
    ],
    related: ["how-to-make-gst-invoice-in-india", "what-is-hsn-code", "best-billing-software-india"],
  },
  // ───────────────────────────────────────────── 3
  {
    slug: "what-is-hsn-code",
    title: "What is an HSN Code? Complete Guide for Indian Businesses",
    h1: "What is an HSN Code? (India 2026)",
    description:
      "HSN codes explained simply — what they are, who needs them, how to find yours, and how many digits to use based on your turnover.",
    keywords: "hsn code, what is hsn code, hsn code india, hsn code list, hsn vs sac",
    author: "InvoiceSetu Team",
    published: TODAY,
    updated: TODAY,
    readMin: 5,
    category: "GST Compliance",
    hero: "If your invoice has goods on it, you need an HSN code. Here's how to find yours in 2 minutes.",
    sections: [
      { kind: "h2", text: "Definition" },
      { kind: "p", text: "HSN stands for Harmonised System of Nomenclature — a global product classification system used by 200+ countries. India adopted it under GST to standardise how goods are taxed. Each HSN code represents a specific product category." },
      { kind: "h2", text: "How many digits do you need?" },
      { kind: "table", headers: ["Annual Turnover (FY)", "HSN digits required"], rows: [
        ["Up to ₹5 Cr (B2B)", "4 digits"],
        ["Up to ₹5 Cr (B2C)", "Optional (4 digits recommended)"],
        ["Above ₹5 Cr", "6 digits"],
        ["Exports / Imports", "8 digits"],
      ]},
      { kind: "h2", text: "HSN vs SAC" },
      { kind: "p", text: "HSN is for GOODS. SAC (Services Accounting Code) is for SERVICES. Both are mandatory under GST and follow similar rules. SAC starts with 99 — e.g. '998314' for IT consultancy." },
      { kind: "h2", text: "How to find your HSN code" },
      { kind: "ol", items: [
        "Identify the broad category (e.g. textiles, machinery, food).",
        "Search the GST portal HSN finder OR use a tool with built-in HSN master.",
        "Match the most specific 4-6 digit code that describes your product.",
        "Cross-check the GST rate notification for that code (5% / 12% / 18% / 28%).",
      ] },
      { kind: "callout", tone: "blue", title: "Time-saver", text: "InvoiceSetu's HSN master has 6,000+ codes searchable by keyword. Type 'cement' → returns 2523 instantly." },
      { kind: "h2", text: "HSN Summary on Invoices" },
      { kind: "p", text: "If your turnover is above ₹5 Cr, every invoice must carry an HSN summary table at the bottom showing total taxable value and tax per HSN code. We auto-include this on every Tax Invoice." },
    ],
    faqs: [
      { q: "What if I get the HSN code wrong?", a: "Penalty up to ₹50,000 per invoice (₹25,000 CGST + ₹25,000 SGST). Always double-check before sending." },
      { q: "Are HSN codes the same across all states?", a: "Yes — HSN is a national standard. State doesn't matter, but the GST rate (slab) is uniform across India." },
      { q: "Can one product have multiple HSN codes?", a: "No — one product = one most-specific HSN code. Use the one that best describes the actual product." },
    ],
    related: ["how-to-make-gst-invoice-in-india", "difference-between-bill-and-tax-invoice", "gstr1-basics-for-business-owners"],
  },
  // ───────────────────────────────────────────── 4
  {
    slug: "how-to-send-professional-quotation",
    title: "How to Send a Professional Quotation (And Win More Clients)",
    h1: "How to Send a Professional Quotation",
    description:
      "Step-by-step guide to writing and sending a professional quotation in India — what to include, validity, terms, and how to convert quotes into invoices.",
    keywords: "how to send quotation, professional quotation format, quotation template india, win more clients with quotations",
    author: "InvoiceSetu Team",
    published: TODAY,
    updated: TODAY,
    readMin: 5,
    category: "Sales",
    hero: "A polished quotation closes deals faster. Here's the format, the tone and the follow-up flow that actually works.",
    sections: [
      { kind: "h2", text: "What is a quotation?" },
      { kind: "p", text: "A quotation is a formal price offer to a prospect — what they will pay if they accept. It's NOT an invoice (no obligation to pay yet) and NOT a proforma (no order confirmed). It's the start of every B2B sales cycle." },
      { kind: "h2", text: "10 things every quotation must have" },
      { kind: "ol", items: [
        "Your business name, logo and contact",
        "Quotation number (sequential)",
        "Issue date and validity period (e.g. valid until 30 days)",
        "Recipient name + address",
        "Itemised list with description, qty, rate, total",
        "GST breakdown (if applicable)",
        "Subtotal, taxes, grand total",
        "Payment terms (e.g. 50% advance, 50% on delivery)",
        "Special conditions / scope exclusions",
        "Signature and contact for follow-up",
      ] },
      { kind: "h2", text: "Pricing strategy" },
      { kind: "p", text: "Always offer ONE price, not ranges. If a buyer asks for range, you've lost the framing. Use milestone billing for projects above ₹50,000 — quote the total but bill 30/40/30 to maintain cash flow." },
      { kind: "h2", text: "The follow-up flow" },
      { kind: "ol", items: [
        "Day 0 — Send via share link (not just PDF) so you see when they open.",
        "Day 2 — Soft check-in via WhatsApp: 'Did you have a chance to review?'",
        "Day 5 — Offer to walk through over a 10-min call.",
        "Day 10 — Polite expiry reminder if validity is closing.",
        "Day 14 — Close the loop: 'Is this still relevant or should we revisit later?'",
      ] },
      { kind: "callout", tone: "blue", title: "The data is clear", text: "Quotes with a public share link convert 2.4x higher than email-attached PDFs because clients can review, approve and ask questions inline — no scanned signatures, no back-and-forth." },
    ],
    faqs: [
      { q: "How long should a quotation be valid?", a: "30 days is standard. For commodity-priced items (steel, cement) keep it 7-10 days because rates fluctuate." },
      { q: "Should I add GST on a quotation?", a: "Yes — show GST clearly so buyers see the actual landed cost, not a surprise on the invoice." },
      { q: "Can a quotation be legally enforced?", a: "Once accepted in writing (or by the buyer paying advance), the quote becomes an offer-acceptance contract. Hence keep quote terms clear." },
    ],
    related: ["how-to-track-pending-payments", "invoice-vs-quotation-difference"],
  },
  // ───────────────────────────────────────────── 5
  {
    slug: "how-to-track-pending-payments",
    title: "How to Track Pending Payments (Without Awkward Reminders)",
    h1: "How to Track Pending Payments",
    description:
      "Practical guide to tracking and recovering pending payments from clients in India — automation, ageing buckets, polite reminders, and the legal route.",
    keywords: "track pending payments, recover overdue invoices, accounts receivable india, polite reminders for unpaid invoices",
    author: "InvoiceSetu Team",
    published: TODAY,
    updated: TODAY,
    readMin: 5,
    category: "Cash Flow",
    hero: "Late payments kill SMBs. Here's a calm, professional system that keeps cash flowing without burning client relationships.",
    sections: [
      { kind: "h2", text: "Why payments go late" },
      { kind: "p", text: "It's almost never malice. Buyers forget, lose the PDF, route it to the wrong department, or are themselves waiting on cash. A polite, automated nudge solves 80% of this." },
      { kind: "h2", text: "The 4-bucket ageing system" },
      { kind: "table", headers: ["Days Overdue", "Action"], rows: [
        ["0–30", "Polite WhatsApp + Email reminder. Resend invoice + payment link."],
        ["31–60", "Call the AP contact. Offer EMI / part-payment options."],
        ["61–90", "Escalate to the buyer's procurement head. Pause new orders."],
        ["90+", "Send a formal demand letter. Consider MSME Samadhaan or commercial court."],
      ]},
      { kind: "h2", text: "What to write in a polite reminder" },
      { kind: "quote", text: "Hi [Name], hope you're well. Just a quick reminder — Invoice INV/2026-27/0042 (₹45,000) is now 15 days past due. Could you let me know an expected date? Happy to share the original invoice again. Thanks!" },
      { kind: "callout", tone: "blue", title: "Automate the boring part", text: "InvoiceSetu auto-sends polite reminders on day 3, 7, 14 and 28 — with the invoice + payment link attached every time. Less awkwardness, more cash." },
      { kind: "h2", text: "Legal options for recovery" },
      { kind: "ul", items: [
        "MSME Samadhaan portal (free, fast for MSMEs) — buyer must pay within 45 days or face 3x bank interest.",
        "Commercial Courts (if invoice value > ₹3 lakh).",
        "Lok Adalat (lower-value disputes, settled in one sitting).",
        "Insolvency & Bankruptcy Code (₹1 Cr+ default — last resort).",
      ] },
    ],
    faqs: [
      { q: "When should I write off a payment as bad debt?", a: "After 180+ days with no response and no recovery path. Move it to bad debt to claim the GST refund (file via GSTR-1 amendment)." },
      { q: "Can I charge interest on late payments?", a: "Yes — under the MSMED Act, MSMEs can charge 3x the bank rate for delayed payments. Mention this clause in your terms upfront." },
      { q: "Should I stop deliveries to non-paying clients?", a: "Yes, after 60+ days unless there's a strong reason. Continuing deliveries to chronic late-payers worsens cash flow." },
    ],
    related: ["how-to-send-professional-quotation", "best-billing-software-india"],
  },
  // ───────────────────────────────────────────── 6
  {
    slug: "invoice-vs-quotation-difference",
    title: "Invoice vs Quotation — What's the Difference?",
    h1: "Invoice vs Quotation: When to Use What",
    description:
      "Clear, simple comparison of invoice vs quotation vs proforma invoice — when to issue each, legal status, and the right order in a sales cycle.",
    keywords: "invoice vs quotation, quotation vs invoice, proforma vs invoice, sales document order",
    author: "InvoiceSetu Team",
    published: TODAY,
    updated: TODAY,
    readMin: 4,
    category: "Sales",
    hero: "Three documents, three different stages of a deal. Mix them up and you'll either lose deals or face audit issues.",
    sections: [
      { kind: "h2", text: "The natural sales flow" },
      { kind: "ol", items: [
        "Quotation — 'Here's what it would cost if you bought today.' No obligation either way.",
        "Proforma Invoice — 'You confirmed the order; here's the bill before dispatch.' Used for advance collection.",
        "Tax Invoice — 'Goods/services delivered; please pay.' GST-compliant, ITC-eligible.",
      ] },
      { kind: "h2", text: "Side-by-side comparison" },
      { kind: "table", headers: ["", "Quotation", "Proforma", "Tax Invoice"], rows: [
        ["Stage", "Pre-sale", "Order confirmed, pre-delivery", "Post-delivery"],
        ["Legally binding?", "No", "No (offer only)", "Yes — payment due"],
        ["GST claim?", "Not yet", "Not yet", "Yes — buyer can claim ITC"],
        ["Numbering", "Optional sequence", "Separate sequence (PI/...)", "Mandatory sequence under GST"],
        ["Validity", "Usually 30 days", "Until acceptance / advance", "Until paid or written off"],
      ]},
      { kind: "callout", tone: "amber", title: "Common mistake", text: "Issuing a Tax Invoice before goods are delivered. Under GST, the tax point is the EARLIER of dispatch or invoice date. If you invoice and then never deliver, you owe the GST anyway." },
    ],
    faqs: [
      { q: "Can a quotation become an invoice automatically?", a: "Yes, in good tools. InvoiceSetu lets you 'Convert to Invoice' in one click — full amount or milestone slice — keeping the link to the original quote." },
      { q: "Do I need a separate numbering for proforma invoices?", a: "Yes. Use a different prefix (e.g. PI/2026-27/...) so it never clashes with your tax invoice sequence." },
    ],
    related: ["how-to-send-professional-quotation", "how-to-make-gst-invoice-in-india"],
  },
  // ───────────────────────────────────────────── 7
  {
    slug: "gstr1-basics-for-business-owners",
    title: "GSTR-1 Basics — A Simple Guide for Business Owners",
    h1: "GSTR-1: A Plain-English Guide for Business Owners",
    description:
      "Understand GSTR-1 in simple terms — what it is, who must file, due dates, and the 5-minute monthly process if your invoicing tool is set up right.",
    keywords: "gstr-1 guide, gstr1 filing, monthly gst return, gstr1 due date, gstr1 b2b",
    author: "InvoiceSetu Team",
    published: TODAY,
    updated: TODAY,
    readMin: 5,
    category: "GST Compliance",
    hero: "GSTR-1 sounds scary but is mostly auto-fillable from your invoices. If you spend more than 30 mins/month on it, your tooling is wrong.",
    sections: [
      { kind: "h2", text: "What is GSTR-1?" },
      { kind: "p", text: "GSTR-1 is the monthly (or quarterly) return that lists all OUTWARD supplies — meaning every Tax Invoice, Credit Note, and Debit Note you issued in the period. It's how the government cross-checks ITC claims." },
      { kind: "h2", text: "Who must file?" },
      { kind: "ul", items: [
        "Every GST-registered taxpayer (mandatory, even if NIL)",
        "Composition dealers — file CMP-08 instead",
        "Input Service Distributors — file GSTR-6 instead",
      ] },
      { kind: "h2", text: "Due dates (FY 2026-27)" },
      { kind: "table", headers: ["Filing Frequency", "Eligibility", "Due"], rows: [
        ["Monthly", "Turnover > ₹5 Cr OR opted in", "11th of next month"],
        ["Quarterly (QRMP)", "Turnover ≤ ₹5 Cr", "13th of month after quarter ends"],
      ]},
      { kind: "h2", text: "What's inside GSTR-1?" },
      { kind: "ol", items: [
        "B2B invoices (where buyer has GSTIN)",
        "B2C Large invoices (≥ ₹2.5 lakh)",
        "B2C Small invoices (< ₹2.5 lakh, summary)",
        "Credit Notes / Debit Notes",
        "Exports & Deemed Exports",
        "Advances received",
        "HSN-wise summary",
        "Documents issued (invoice ranges)",
      ] },
      { kind: "callout", tone: "blue", title: "If you use a good tool", text: "InvoiceSetu auto-generates the GSTR-1 JSON / Excel from your month's invoices in one click. Your CA reviews and uploads — total time: 10 minutes." },
      { kind: "h2", text: "Penalty for late filing" },
      { kind: "p", text: "₹50/day (₹25 CGST + ₹25 SGST) for normal taxpayers; ₹20/day for NIL returns. Capped at ₹5,000. Late filing also blocks your buyer from claiming ITC — which damages relationships." },
    ],
    faqs: [
      { q: "Can I edit GSTR-1 after filing?", a: "Not the same return. You make corrections in the next period via amendments (B2B-A / B2C-A). Wrong invoice details propagate downstream until corrected." },
      { q: "What's GSTR-2B and how does it relate?", a: "GSTR-2B is auto-generated for the BUYER from all sellers' GSTR-1 filings. Buyers reconcile their purchases against GSTR-2B to claim correct ITC." },
      { q: "I had no sales — do I still file?", a: "Yes — file a NIL GSTR-1. Skipping triggers penalty notices." },
    ],
    related: ["how-to-make-gst-invoice-in-india", "what-is-hsn-code", "best-billing-software-india"],
  },
  // ───────────────────────────────────────────── 8
  {
    slug: "best-billing-software-india",
    title: "Best Billing Software in India for Small Businesses (2026)",
    h1: "Best Billing Software in India (2026)",
    description:
      "Honest comparison of the top billing/invoicing software for Indian SMBs in 2026 — Tally, Zoho, Vyapar, ClearTax, InvoiceSetu — pricing, features, who it's for.",
    keywords: "best billing software india, best invoicing software, tally vs zoho, gst billing software comparison",
    author: "InvoiceSetu Team",
    published: TODAY,
    updated: TODAY,
    readMin: 7,
    category: "Tools",
    hero: "Five contenders, five very different ideal buyers. Here's how to pick — without wasting a month on a free trial that's wrong for you.",
    sections: [
      { kind: "h2", text: "Quick comparison" },
      { kind: "table", headers: ["Tool", "Best For", "Starts At", "Works on Phone", "GST-Native"], rows: [
        ["InvoiceSetu", "SMBs, freelancers, traders, CAs", "Free", "Yes", "Yes"],
        ["Tally", "Mid-size businesses with accountant", "₹18,000/yr (Prime)", "No", "Yes"],
        ["Zoho Invoice", "Service businesses, freelancers", "Free / ₹999/mo (Books)", "Yes", "Yes"],
        ["Vyapar", "Retail / kirana / shop owners", "₹3,499/yr", "Yes", "Yes"],
        ["ClearTax", "Compliance-heavy + filing", "₹2,400/yr", "Partial", "Yes"],
      ]},
      { kind: "h2", text: "Honest take on each" },
      { kind: "h3", text: "InvoiceSetu" },
      { kind: "p", text: "Best if you want a modern, India-first tool with quotation + milestone billing + CA collaboration baked in. Free forever plan is genuinely useful (not crippled). Clean UI that works equally well on desktop and phone." },
      { kind: "h3", text: "Tally" },
      { kind: "p", text: "Best if your accountant insists on it. Powerful, but desktop-bound and dated UI. Steep learning curve unless you have a Tally operator." },
      { kind: "h3", text: "Zoho Invoice / Books" },
      { kind: "p", text: "Best for service businesses. Good multi-currency, time tracking. Pricing climbs fast as you add features." },
      { kind: "h3", text: "Vyapar" },
      { kind: "p", text: "Best for kirana / retail / wholesale shop owners. Strong stock + barcode + thermal printing. Less suited for service-based or project-based businesses." },
      { kind: "h3", text: "ClearTax" },
      { kind: "p", text: "Best if filing is your bottleneck. Strong GSTR-1 / 3B / 9 compliance. Invoicing UI is secondary." },
      { kind: "h2", text: "Decision in 30 seconds" },
      { kind: "ul", items: [
        "I sell services, want polish + mobile → InvoiceSetu",
        "I need stock + barcode + thermal print → Vyapar",
        "My CA loves Tally → Tally",
        "I'm a CA needing client filings dashboard → ClearTax",
        "I need multi-currency for international clients → Zoho",
      ] },
    ],
    faqs: [
      { q: "Is free billing software safe to use?", a: "Yes — if it's from a known vendor with encryption, daily backups, and clear data export. Avoid 'free' tools that lock you in or sell your data." },
      { q: "Can I migrate from Tally to InvoiceSetu?", a: "Yes — Tally XML import/export is on InvoiceSetu's Pro plan. Customers, products, opening balances all transfer." },
    ],
    related: ["how-to-make-gst-invoice-in-india", "how-to-track-pending-payments"],
  },
  // ───────────────────────────────────────────── 9
  {
    slug: "freelancer-invoice-guide-india",
    title: "How Freelancers Should Invoice Clients (India Guide 2026)",
    h1: "Freelancer Invoicing in India — A Practical Guide",
    description:
      "Complete guide for Indian freelancers — when you need GST, how to invoice domestic + foreign clients, TDS handling, and getting paid faster.",
    keywords: "freelancer invoice india, how to invoice as freelancer, freelancer gst, tds for freelancers",
    author: "InvoiceSetu Team",
    published: TODAY,
    updated: TODAY,
    readMin: 6,
    category: "Freelancing",
    hero: "If you freelance in India, your invoicing system is your cash flow. Get this right and the rest of the business gets simpler.",
    sections: [
      { kind: "h2", text: "Do you need a GSTIN?" },
      { kind: "p", text: "If your annual turnover crosses ₹20 lakh (₹10 lakh in special states), GST registration is mandatory. Below that, optional. Most freelancers stay under and use Bills of Supply — perfectly legal." },
      { kind: "h2", text: "Domestic clients vs foreign clients" },
      { kind: "table", headers: ["Client Type", "Document", "GST", "Other"], rows: [
        ["India, GSTIN-registered", "Tax Invoice (if you have GSTIN) OR Bill of Supply", "Yes / No", "Place of supply matters"],
        ["India, no GSTIN", "Bill of Supply", "No", "TDS may apply"],
        ["Foreign client (USD/EUR/etc.)", "Export Invoice (zero-rated)", "0% (with LUT)", "FIRC for FEMA"],
      ]},
      { kind: "h2", text: "TDS — what to expect" },
      { kind: "p", text: "Most professional services attract 10% TDS under Section 194J (or 1% under 194C for contract work). Buyer deducts and deposits — you get a TDS certificate (Form 16A). Mention TDS in your invoice notes so the net payable is clear." },
      { kind: "callout", tone: "blue", title: "Net Payable example", text: "Invoice ₹50,000 + 18% GST = ₹59,000. TDS on professional fee @ 10% = ₹5,000. Buyer pays ₹54,000. You file ₹5,000 against your ITR. InvoiceSetu auto-calculates this." },
      { kind: "h2", text: "Getting paid faster" },
      { kind: "ol", items: [
        "Send a payment link in every invoice (Razorpay UPI / card).",
        "Set Net 15 — not Net 30 — for new clients.",
        "Ask for 25% advance on projects > ₹25,000.",
        "Auto-reminders at day 3, 7, 14.",
        "Bill on the 1st of each month if you're on retainer (avoid month-end).",
      ] },
      { kind: "h2", text: "What about exports?" },
      { kind: "p", text: "File a Letter of Undertaking (LUT) on the GST portal once a year — it lets you export at 0% GST without paying-and-claiming refund. Without LUT, you charge IGST and apply for refund every quarter." },
    ],
    faqs: [
      { q: "If I'm under ₹20L, can I voluntarily register for GST?", a: "Yes — useful if you mostly bill GST-registered clients (they prefer Tax Invoices for ITC). But you'll have to file GSTR-1, 3B, etc. monthly." },
      { q: "Should I issue separate invoices per project?", a: "Yes for projects > ₹10,000. For smaller recurring work (e.g. monthly retainer), one consolidated monthly invoice is cleaner." },
      { q: "How do I handle payment in USD?", a: "Use Wise, Stripe, Razorpay International, or your bank. Bank charges + currency conversion can eat 4-6% — factor that into your USD pricing." },
    ],
    related: ["how-to-make-gst-invoice-in-india", "how-to-track-pending-payments", "best-billing-software-india"],
  },
  // ───────────────────────────────────────────── 10
  {
    slug: "what-is-sac-code",
    title: "What is a SAC Code? Service Codes Under GST Explained",
    h1: "What is a SAC Code? (India 2026)",
    description:
      "SAC code explained — what it is, where to use it on a service invoice, list of common SAC codes for IT, consulting, design, education, and more.",
    keywords: "sac code, what is sac code, sac code list, service accounting code india, sac vs hsn",
    author: "InvoiceSetu Team",
    published: TODAY,
    updated: TODAY,
    readMin: 4,
    category: "GST Compliance",
    hero: "If you sell services, you need a SAC code. Here are the most common ones — and how to find yours.",
    sections: [
      { kind: "h2", text: "What is a SAC code?" },
      { kind: "p", text: "SAC stands for Services Accounting Code. It's a 6-digit code that classifies services for GST purposes. All SAC codes start with 99 — that's how you tell them apart from HSN (which is for goods)." },
      { kind: "h2", text: "Common SAC codes" },
      { kind: "table", headers: ["Service", "SAC Code", "GST Rate"], rows: [
        ["IT consulting / software development", "998314", "18%"],
        ["Web design / app design", "998313", "18%"],
        ["Graphic design", "998391", "18%"],
        ["Photography services", "998387", "18%"],
        ["Tuition / coaching (private)", "999293", "18%"],
        ["Legal services", "998213", "18%"],
        ["CA / accounting services", "998222", "18%"],
        ["Interior design", "998391", "18%"],
        ["Construction (contractor)", "9954xx (varies)", "5% / 12% / 18%"],
        ["Restaurant / catering", "996331", "5% (no ITC) / 18% (5-star)"],
        ["Cab / taxi (passenger transport)", "996411", "5% (no ITC)"],
        ["Beauty / salon services", "999722", "18%"],
      ]},
      { kind: "h2", text: "How is SAC different from HSN?" },
      { kind: "ul", items: [
        "HSN — for GOODS (8 digits internationally, 4 or 6 in India)",
        "SAC — for SERVICES (6 digits, all start with 99)",
        "Both are mandatory under GST",
        "Both have a similar 'specificity' rule: pick the most specific match",
      ] },
      { kind: "callout", tone: "blue", title: "Save time", text: "If you're a freelancer or consultant, SAC 998314 (IT/software) or 998391 (creative design) covers most billing. Set it once in your default item template and forget about it." },
    ],
    faqs: [
      { q: "Can I use the same SAC code for different services on one invoice?", a: "Yes if the services genuinely fall under the same code. Otherwise use separate line items with different SAC codes." },
      { q: "What's the SAC for export of services?", a: "Same SAC as domestic — but the place of supply rules and 0% rate (with LUT) make it an 'export of services'." },
      { q: "Is SAC mandatory below ₹20L turnover?", a: "Only if you're GST-registered. If you're below threshold and unregistered, you don't issue Tax Invoices, so SAC isn't needed." },
    ],
    related: ["what-is-hsn-code", "how-to-make-gst-invoice-in-india", "freelancer-invoice-guide-india"],
  },
];
